// Workspace Documents — a Notion/Confluence-style personal knowledge surface
// independent of projects. Two pages:
//   <DocumentsPage>     — list (Mine / Shared with me filters)
//   <DocumentEditPage>  — single doc editor (title + rich text + share)
//
// Renders inside the main app shell so the sidebar stays visible.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, Plus, Lock, Users, Globe, Trash, MagnifyingGlass,
  CaretLeft, Check,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import RichEditor from '../components/RichEditor';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useImagePaste } from '../hooks/useImagePaste';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function visibilityMeta(v) {
  if (v === 'org') return { icon: Globe, label: 'Org-wide', color: 'bg-blue-100 text-blue-700' };
  if (v === 'shared') return { icon: Users, label: 'Shared', color: 'bg-violet-100 text-violet-700' };
  return { icon: Lock, label: 'Private', color: 'bg-zinc-100 text-zinc-600' };
}

function fmtRelative(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

// ─── List page ────────────────────────────────────────────────────────
export default function DocumentsPage() {
  const navigate = useNavigate();
  const { user, getOrgHeaders } = useAuth();
  const { hasCap } = useSubscription();
  // Documents is a paid feature. If a Free-plan user lands here directly
  // (typed URL / stale bookmark), bounce them to /billing — the backend
  // returns 402 for the workspace-docs endpoints anyway, but redirecting
  // gives a kinder UX than an empty list with API errors in the console.
  useEffect(() => {
    if (hasCap && hasCap('workspace_docs') === false) {
      navigate('/billing', { replace: true });
    }
  }, [hasCap, navigate]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all'); // all | mine | shared

  const h = () => ({ withCredentials: true, headers: getOrgHeaders() });

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/workspace-docs`, h());
      setDocs(data || []);
    } catch (e) { toast.error('Failed to load documents'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const createNew = async () => {
    try {
      const { data } = await axios.post(`${API}/workspace-docs`, { title: 'Untitled' }, h());
      navigate(`/documents/${data.doc_id}`);
    } catch (e) { toast.error('Failed to create document'); }
  };

  const filtered = useMemo(() => {
    let list = docs;
    if (filter === 'mine') list = list.filter(d => d.is_owner);
    else if (filter === 'shared') list = list.filter(d => !d.is_owner);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(d => (d.title || '').toLowerCase().includes(q) || (d.excerpt || '').toLowerCase().includes(q));
    }
    return list;
  }, [docs, query, filter]);

  return (
    <div className="flex-1 overflow-auto bg-white" data-testid="documents-page">
      <div className="max-w-5xl mx-auto px-8 py-10">
        {/* Header */}
        <div className="flex items-end justify-between mb-8">
          <div>
            <h1 className="font-['Outfit'] text-3xl font-semibold text-zinc-900 tracking-tight">Documents</h1>
            <p className="text-[13px] text-zinc-500 mt-1">
              Personal & shared docs across your workspace, independent of any project.
            </p>
          </div>
          <Button onClick={createNew} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-doc-button">
            <Plus size={16} className="mr-1.5" weight="bold" /> New Document
          </Button>
        </div>

        {/* Filters + search */}
        <div className="flex items-center gap-3 mb-5">
          <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-1">
            {[
              { id: 'all',    label: 'All' },
              { id: 'mine',   label: 'Mine' },
              { id: 'shared', label: 'Shared with me' },
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`docs-filter-${f.id}`}
                className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-colors ${filter === f.id ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-600 hover:text-zinc-900'}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex-1 relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search documents..."
              className="pl-9 h-9 text-[13px] bg-zinc-50 border-zinc-200"
              data-testid="docs-search-input"
            />
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-20 text-[13px] text-zinc-400">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 border-2 border-dashed border-zinc-200 rounded-2xl">
            <FileText size={36} className="mx-auto text-zinc-300 mb-3" />
            <p className="text-[14px] font-medium text-zinc-700">No documents yet</p>
            <p className="text-[12px] text-zinc-500 mt-1 mb-4">Start writing — your docs are private until you share them.</p>
            <Button onClick={createNew} variant="outline" data-testid="empty-create-doc">
              <Plus size={14} className="mr-1" /> Create your first document
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(d => {
              const vm = visibilityMeta(d.visibility);
              const VisIcon = vm.icon;
              return (
                <button
                  key={d.doc_id}
                  onClick={() => navigate(`/documents/${d.doc_id}`)}
                  data-testid={`doc-card-${d.doc_id}`}
                  className="text-left rounded-xl border border-zinc-200 bg-white p-4 hover:border-blue-300 hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <FileText size={18} className="text-zinc-400 mt-0.5 flex-shrink-0" />
                    <Badge className={`text-[10px] ${vm.color}`}>
                      <VisIcon size={9} className="mr-1" weight="fill" /> {vm.label}
                    </Badge>
                  </div>
                  <h3 className="font-semibold text-[14px] text-zinc-900 mb-1 line-clamp-2">{d.title}</h3>
                  {d.excerpt && (
                    <p className="text-[11px] text-zinc-500 line-clamp-2 leading-snug">{d.excerpt}</p>
                  )}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-100">
                    <span className="text-[10px] text-zinc-500">{fmtRelative(d.updated_at)}</span>
                    {!d.is_owner && <span className="text-[10px] text-violet-600 font-medium">Shared with you</span>}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Edit page ────────────────────────────────────────────────────────
export function DocumentEditPage() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const { user, getOrgHeaders } = useAuth();
  const { uploadImage } = useImagePaste(getOrgHeaders);
  const [doc, setDoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [members, setMembers] = useState([]);
  // Tracks the LAST persisted title/content so blur-autosave can compare
  // against the server's last-known value instead of the live `doc` state
  // (which onChange updates on every keystroke, defeating any equality
  // guard against the live `doc.title` / `doc.content`).
  const lastSavedRef = useRef({ title: '', content: '' });

  const h = () => ({ withCredentials: true, headers: getOrgHeaders() });

  useEffect(() => {
    axios.get(`${API}/workspace-docs/${docId}`, h())
      .then(r => {
        setDoc(r.data);
        lastSavedRef.current = { title: r.data.title || '', content: r.data.content || '' };
      })
      .catch(err => {
        toast.error(err?.response?.data?.detail || 'Failed to load document');
        navigate('/documents');
      });
    // Members list — used by the share dialog and the @-mention picker.
    // Endpoint is mounted at `/api/members` (organizations router uses the
    // unprefixed path); calling /api/organizations/members 404s silently.
    axios.get(`${API}/members`, h())
      .then(r => setMembers(r.data || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/workspace-docs/${docId}`, patch, h());
      setDoc(data);
      lastSavedRef.current = { title: data.title || '', content: data.content || '' };
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm('Delete this document? This cannot be undone from the UI.')) return;
    try {
      await axios.delete(`${API}/workspace-docs/${docId}`, h());
      toast.success('Document deleted');
      navigate('/documents');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  if (!doc) return <div className="flex-1 flex items-center justify-center text-[13px] text-zinc-400">Loading...</div>;
  const vm = visibilityMeta(doc.visibility);
  const VisIcon = vm.icon;
  const canEdit = true;     // any visible-to-user doc is editable
  const isOwner = doc.is_owner;

  return (
    <div className="flex-1 overflow-auto bg-white" data-testid="document-edit-page">
      <div className="max-w-[1100px] mx-auto px-8 py-8">
        {/* Header bar */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/documents')}
            className="flex items-center gap-1.5 text-[12px] text-zinc-500 hover:text-zinc-900 transition-colors"
            data-testid="back-to-docs"
          >
            <CaretLeft size={14} weight="bold" /> Documents
          </button>
          <div className="flex items-center gap-2">
            <Badge className={`text-[10px] ${vm.color}`}>
              <VisIcon size={9} className="mr-1" weight="fill" /> {vm.label}
              {doc.visibility === 'shared' && ` · ${doc.shared_with?.length || 0}`}
            </Badge>
            {saving && <span className="text-[10px] text-zinc-400">Saving...</span>}
            {isOwner && (
              <Button size="sm" variant="outline" onClick={() => setShareOpen(true)} data-testid="share-doc-button">
                <Users size={12} className="mr-1" /> Share
              </Button>
            )}
            {isOwner && (
              <Button size="sm" variant="ghost" onClick={remove} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="delete-doc-button">
                <Trash size={12} />
              </Button>
            )}
          </div>
        </div>

        {/* Title */}
        <Input
          value={doc.title || ''}
          onChange={e => setDoc({ ...doc, title: e.target.value })}
          onBlur={e => {
            const v = e.target.value.trim();
            if (v && v !== lastSavedRef.current.title) save({ title: v });
          }}
          disabled={!canEdit}
          maxLength={200}
          placeholder="Untitled"
          className="text-3xl font-bold border-none px-0 focus-visible:ring-0 mb-6 font-['Outfit']"
          data-testid="doc-title-input"
        />

        {/* Rich text body — mentionItems lets users @-tag teammates; on save
            the backend parses TipTap mention spans and emails the newly
            tagged users (it diffs against the doc's previous content so we
            don't spam on every keystroke). */}
        <RichEditor
          value={doc.content || ''}
          onChange={(html) => setDoc({ ...doc, content: html })}
          onBlur={(html) => { if (html !== lastSavedRef.current.content) save({ content: html }); }}
          minHeight={520}
          placeholder="Start writing... Use @ to mention a teammate, or paste a screenshot to attach an image."
          mentionItems={members}
          uploadImage={uploadImage}
        />
      </div>

      {/* Share dialog */}
      <ShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        doc={doc}
        members={members.filter(m => m.user_id !== user?.user_id)}
        onSave={async (patch) => {
          await save(patch);
          setShareOpen(false);
          toast.success('Sharing updated');
        }}
      />
    </div>
  );
}

// ─── Share dialog ─────────────────────────────────────────────────────
function ShareDialog({ open, onClose, doc, members, onSave }) {
  const [visibility, setVisibility] = useState('private');
  const [selected, setSelected] = useState([]);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (open) {
      setVisibility(doc?.visibility || 'private');
      setSelected(doc?.shared_with || []);
      setQuery('');
    }
  }, [open, doc]);

  const filteredMembers = useMemo(() => {
    if (!query.trim()) return members;
    const q = query.trim().toLowerCase();
    return members.filter(m => (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q));
  }, [members, query]);

  const toggle = (uid) => {
    setSelected(s => s.includes(uid) ? s.filter(x => x !== uid) : [...s, uid]);
  };

  if (!doc) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle>Share document</DialogTitle>
        </DialogHeader>

        {/* Visibility radios */}
        <div className="space-y-2 my-4">
          {[
            { id: 'private', icon: Lock,   label: 'Private',  desc: 'Only you can see it' },
            { id: 'shared',  icon: Users,  label: 'Shared',   desc: 'You + specific members' },
            { id: 'org',     icon: Globe,  label: 'Org-wide', desc: 'Everyone in the workspace' },
          ].map(opt => {
            const Icon = opt.icon;
            const active = visibility === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => setVisibility(opt.id)}
                data-testid={`share-visibility-${opt.id}`}
                className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all ${active ? 'border-blue-300 bg-blue-50/50' : 'border-zinc-200 hover:border-zinc-300'}`}
              >
                <div className="flex items-center gap-2">
                  <Icon size={14} className={active ? 'text-blue-600' : 'text-zinc-500'} />
                  <span className="text-[13px] font-medium text-zinc-900">{opt.label}</span>
                  {active && <Check size={14} className="ml-auto text-blue-600" weight="bold" />}
                </div>
                <p className="text-[11px] text-zinc-500 mt-0.5 ml-6">{opt.desc}</p>
              </button>
            );
          })}
        </div>

        {/* Member picker (only relevant for shared) */}
        {visibility === 'shared' && (
          <div className="border-t pt-4">
            <Input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search members..."
              className="mb-2 h-9 text-[13px]"
              data-testid="share-member-search"
            />
            <div className="max-h-56 overflow-y-auto rounded-lg border border-zinc-200">
              {filteredMembers.length === 0 && (
                <p className="text-[12px] text-zinc-400 py-6 text-center">No members found</p>
              )}
              {filteredMembers.map(m => (
                <label
                  key={m.user_id}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-zinc-50 cursor-pointer text-[12px]"
                  data-testid={`share-member-${m.user_id}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(m.user_id)}
                    onChange={() => toggle(m.user_id)}
                  />
                  <span className="font-medium text-zinc-900">{m.name || m.email}</span>
                  <span className="text-zinc-500 text-[11px]">{m.email}</span>
                </label>
              ))}
            </div>
            <p className="text-[10px] text-zinc-500 mt-2">{selected.length} member{selected.length === 1 ? '' : 's'} selected · they can read and edit</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onSave({ visibility, shared_with: visibility === 'shared' ? selected : [] })}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="share-save-button"
          >
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
